import { SetStateAction, useCallback, useState } from 'react';

import { CollectionProgramAsset, useRailSettings, useSeriesCatchUpRailCollection } from '@kaltura-ott/tvpil-shared';

import { RailProviderData, RailSettings } from 'components/widgets/Rail/components/RailProvider/types';
import { useHandleSkeletonRef } from 'components/widgets/Rail/hooks/useHandleSkeletonRef/useHandleSkeletonRef';
import { DEFAULT_IMAGE_PARAMS, RAIL_PAGE_SIZE } from 'consts';
import { useCheckDataIsEmpty } from 'hooks/common/useCheckDataIsEmpty/useCheckDataIsEmpty';
import { useRhinoErrorReaction } from 'hooks/common/useRhinoErrorReaction';

function useSeriesCatchUpRail(referenceId: string) {
  const originalSettings = useRailSettings({ referenceId, ...DEFAULT_IMAGE_PARAMS });
  const originalData = useSeriesCatchUpRailCollection(referenceId, RAIL_PAGE_SIZE);

  const isRailEmpty = useCheckDataIsEmpty(originalData);
  const isRailInvalid = isRailEmpty || Boolean(originalData.error);

  const [shouldPreventDisplay, setShouldPreventDisplay] = useState(true);

  const setShouldPreventDisplayForward = useCallback(
    (value: SetStateAction<boolean>) => setShouldPreventDisplay(value),
    [],
  );

  const data: RailProviderData<CollectionProgramAsset> = {
    items: originalData.items,
    totalCount: originalData.totalCount,
    hasNextPage: originalData.hasNextPage,
    isLoading: originalData.isLoading,
    error: originalData.error,
    fetchNextPage: originalData.fetchNextPage,
    refetch: originalData.refetch,
  };

  const settings: RailSettings = {
    branding: originalSettings?.branding,
    id: originalSettings?.id,
    imageHeight: originalSettings?.imageHeight,
    imageType: originalSettings?.imageType,
    imageWidth: originalSettings?.imageWidth,
    title: originalSettings?.title,
    moreLink: originalSettings?.moreLink,
    shouldShowRating: Boolean(originalSettings?.shouldShowRating),
    shouldShowEntitlementIndicator: Boolean(originalSettings?.shouldShowEntitlementIndicator),
  };

  const handleSkeletonRef = useHandleSkeletonRef(setShouldPreventDisplayForward, data.items, settings);

  useRhinoErrorReaction({ error: originalData.error, refetch: originalData.refetch });

  return {
    data,
    settings: originalSettings ? settings : null,
    isRailInvalid,
    handleSkeletonRef,
    shouldPreventDisplay,
  };
}

export { useSeriesCatchUpRail };
